import { useCallback, useEffect, useRef, useState } from 'react';

import { updateAmplitudeUserAttributes } from 'features/profile/utils/analytics';
import { Platform } from 'react-native';

import { GetMeDocument, useUpdateMeMutation } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import logger from 'utils/logger';

import {
  applyAdvertisingOptOutAfterAccountUpdate,
  applyPendingLiveAdvertisingConsent,
  claimBannerConsentHandoff,
  clearAdvertisingOptOutForAccountUpdate,
  clearSameSessionAdvertisingDenial,
  consumePendingLiveConsentChange,
  getPlatformTrackingConsentState,
  hasSameSessionAdvertisingDenial,
  isOwnAdvertisingConsentWrite,
  markAdvertisingOptOutForAccountUpdate,
  setPlatformTrackingConsent,
  subscribeToIosTrackingDenialPersistenceResult,
  subscribeToPlatformTrackingConsentChange,
  waitForGuestTrackingConsentMerge,
} from './platform-tracking-consent';

export interface UseInterestBasedAdvertisingResult {
  isEnabled: boolean;
  setEnabled: (enabled: boolean) => Promise<void>;
  isSavingPreference: boolean;
  waitForPendingPreferenceSave: () => Promise<void>;
  isDeniedModalOpen: boolean;
  dismissDeniedModal: () => void;
}

/**
 * Implements the Interest-Based Advertising preference sync model from
 * docs/adr/0001-interest-based-advertising-is-a-ceiling-gated-opt-out.md and
 * docs/adr/0002-iba-cross-device-sync-without-a-timestamp.md:
 * - the preference can always be turned off, and an opt-out is written to the other surfaces.
 * - turning it on saves only the surface the guest used.
 * - on an authenticated mount, the account is reconciled against the platform signal, unless
 *   a pending live consent change (captured just before a OneTrust-triggered reload) says
 *   otherwise.
 */
export const useInterestBasedAdvertising = (): UseInterestBasedAdvertisingResult => {
  const { user } = useAuthContext();
  const [updateMeMutation] = useUpdateMeMutation({
    refetchQueries: [{ query: GetMeDocument }],
    awaitRefetchQueries: true,
  });

  const adsPersonalizationOptOut = user?.details?.adsPersonalizationOptOut ?? false;
  const serverEnabled = !adsPersonalizationOptOut;
  const [chosenEnabled, setChosenEnabled] = useState<boolean | null>(null);
  const isEnabled = chosenEnabled ?? serverEnabled;

  const [isDeniedModalOpen, setIsDeniedModalOpen] = useState(false);
  const [isSavingPreference, setIsSavingPreference] = useState(false);
  const hasReconciled = useRef(false);
  const isEnabledRef = useRef(isEnabled);
  const serverEnabledRef = useRef(serverEnabled);
  const ignoreStaleCookieDenial = useRef(false);
  const pendingPreferenceSaveRef = useRef<Promise<void> | null>(null);
  const pendingProviderOptOutSaveRef = useRef<Promise<boolean> | null>(null);
  const resolvePendingProviderOptOutSaveRef = useRef<((saved: boolean) => void) | null>(null);
  isEnabledRef.current = isEnabled;
  serverEnabledRef.current = serverEnabled;

  useEffect(() => {
    if (chosenEnabled !== null && chosenEnabled === serverEnabled) {
      setChosenEnabled(null);
    }
  }, [chosenEnabled, serverEnabled]);

  const persistEnabled = useCallback(
    async (enabled: boolean): Promise<boolean> => {
      try {
        const { data } = await updateMeMutation({
          variables: { input: { adsPersonalizationOptOut: !enabled } },
        });

        if (!data?.updateMe) {
          return false;
        }

        updateAmplitudeUserAttributes({ adsPersonalizationOptOut: !enabled });
        return true;
      } catch (error) {
        logger.error({
          error,
          message: 'Error updating Interest-Based Advertising preference',
        });
        return false;
      }
    },
    [updateMeMutation]
  );

  // ADR-0002 rules 2-3: reconcile once per authenticated mount. (Rule 1, the guest merge, runs
  // separately and once at sign-in - see mergeGuestTrackingConsentOnSignIn in state/auth/index.tsx.)
  useEffect(() => {
    if (!user || hasReconciled.current) {
      return;
    }

    let cancelled = false;

    (async () => {
      await waitForGuestTrackingConsentMerge();
      if (cancelled || hasReconciled.current) {
        return;
      }
      hasReconciled.current = true;

      const enabledNow = isEnabledRef.current;
      const appliedBannerChoice = await applyPendingLiveAdvertisingConsent(
        enabledNow,
        async enabled => {
          isEnabledRef.current = enabled;
          setChosenEnabled(enabled);
          const saved = await persistEnabled(enabled);
          if (!saved) {
            isEnabledRef.current = serverEnabledRef.current;
            setChosenEnabled(null);
          }
          return saved;
        }
      );

      // A banner opt-out is saved here. A banner opt-in leaves the account alone. Either way,
      // skip the cold-load cookie rewrite so the old account value cannot undo the banner.
      const claimedBannerConsentHandoff = claimBannerConsentHandoff();
      if (appliedBannerChoice || claimedBannerConsentHandoff) {
        return;
      }

      // A denial made in this tab already opts the account out at sign-in. Do not write the
      // previous account value back onto the cookie while that account update is in flight.
      if (hasSameSessionAdvertisingDenial()) {
        if (enabledNow && !ignoreStaleCookieDenial.current) {
          await persistEnabled(false);
        }
        return;
      }

      // An account opt-out turns cookies off. An account opt-in does not turn them on.
      const ceilingState = await getPlatformTrackingConsentState();
      if (cancelled) {
        return;
      }
      if (ceilingState === 'granted' && !isEnabledRef.current) {
        if (Platform.OS === 'web') {
          markAdvertisingOptOutForAccountUpdate();
          await applyAdvertisingOptOutAfterAccountUpdate();
          return;
        }

        await setPlatformTrackingConsent(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, isEnabled, persistEnabled]);

  // A cookie opt-out updates the account. A cookie opt-in does not. OneTrust reloads after
  // either banner change so the cookie write is applied.
  useEffect(
    () =>
      subscribeToPlatformTrackingConsentChange(state => {
        // UpdateConsent reports the write we just made. Saving it again starts a second
        // account update, and the opt-out reload cancels that request.
        if (isOwnAdvertisingConsentWrite()) {
          return;
        }
        if (state === 'granted') {
          ignoreStaleCookieDenial.current = false;
          return;
        }
        // Cookies that were already off are not a new opt-out. A profile opt-in must stay saved.
        if (ignoreStaleCookieDenial.current || !isEnabledRef.current) {
          return;
        }

        isEnabledRef.current = false;
        setChosenEnabled(false);
        if (Platform.OS === 'ios') {
          // AuthProvider owns the iOS account write for Ask App Not to Track / Settings denial.
          if (!pendingProviderOptOutSaveRef.current) {
            pendingProviderOptOutSaveRef.current = new Promise(resolve => {
              resolvePendingProviderOptOutSaveRef.current = resolve;
            });
            setIsSavingPreference(true);
          }
          return;
        }
        void persistEnabled(false).then(saved => {
          if (!saved) {
            isEnabledRef.current = serverEnabledRef.current;
            setChosenEnabled(null);
          }
        });
      }),
    [persistEnabled]
  );

  useEffect(() => {
    if (Platform.OS !== 'ios') {
      return () => undefined;
    }

    return subscribeToIosTrackingDenialPersistenceResult(saved => {
      const resolvePendingProviderSave = resolvePendingProviderOptOutSaveRef.current;
      resolvePendingProviderOptOutSaveRef.current = null;
      pendingProviderOptOutSaveRef.current = null;
      resolvePendingProviderSave?.(saved);

      if (saved) {
        if (!pendingPreferenceSaveRef.current) {
          setIsSavingPreference(false);
        }
        return;
      }

      isEnabledRef.current = serverEnabledRef.current;
      setChosenEnabled(null);
      if (!pendingPreferenceSaveRef.current) {
        setIsSavingPreference(false);
      }
    });
  }, []);

  const dismissDeniedModal = useCallback(() => setIsDeniedModalOpen(false), []);
  const waitForPendingPreferenceSave = useCallback(async () => {
    await pendingPreferenceSaveRef.current;
    await pendingProviderOptOutSaveRef.current;
  }, []);

  const setEnabled = useCallback(
    async (enabled: boolean) => {
      const previousSave = pendingPreferenceSaveRef.current;
      const pendingProviderSave = pendingProviderOptOutSaveRef.current;
      const save = (async () => {
        await previousSave;
        await pendingProviderSave;

        // This click is newer than a banner change still waiting in sessionStorage.
        consumePendingLiveConsentChange();

        // Opt-out saves the account and stays on the page. OneTrust and the reload run after
        // Account Info's Update confirms email, OTP, and the profile save.
        isEnabledRef.current = enabled;
        setChosenEnabled(enabled);

        if (Platform.OS === 'web' && !enabled) {
          markAdvertisingOptOutForAccountUpdate();
          const saved = await persistEnabled(false);
          if (!saved) {
            clearAdvertisingOptOutForAccountUpdate();
            isEnabledRef.current = serverEnabledRef.current;
            setChosenEnabled(null);
            return;
          }
          ignoreStaleCookieDenial.current = false;
          return;
        }

        if (!enabled) {
          const saved = await persistEnabled(false);
          if (!saved) {
            isEnabledRef.current = serverEnabledRef.current;
            setChosenEnabled(null);
            return;
          }
          ignoreStaleCookieDenial.current = false;
          await setPlatformTrackingConsent(false);
          return;
        }

        // Opt-in saves the account only. Cookies and the iOS tracking permission stay as they are.
        // A denial already stored for this tab must not write that opt-in back to opted out.
        clearAdvertisingOptOutForAccountUpdate();
        const cookieState = await getPlatformTrackingConsentState();
        if (cookieState === 'denied') {
          ignoreStaleCookieDenial.current = true;
        }
        const saved = await persistEnabled(true);
        if (!saved) {
          ignoreStaleCookieDenial.current = false;
          isEnabledRef.current = serverEnabledRef.current;
          setChosenEnabled(null);
          return;
        }

        clearSameSessionAdvertisingDenial();
      })();

      pendingPreferenceSaveRef.current = save;
      setIsSavingPreference(true);
      try {
        await save;
      } finally {
        if (pendingPreferenceSaveRef.current === save) {
          pendingPreferenceSaveRef.current = null;
          if (!pendingProviderOptOutSaveRef.current) {
            setIsSavingPreference(false);
          }
        }
      }
    },
    [persistEnabled]
  );

  return {
    isEnabled,
    setEnabled,
    isSavingPreference,
    waitForPendingPreferenceSave,
    isDeniedModalOpen,
    dismissDeniedModal,
  };
};
